import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Home, UserRound, Users } from 'lucide-react';
import { AuthContext } from '../App';
import NotificationBell from './NotificationBell';
import { SAFE_BACK_EVENT } from '../hooks/useUnsavedChangesGuard';
import { useMobileKeyboard } from '../hooks/useMobileKeyboard';
import './MobileNavigation.css';

export const MOBILE_QUERY = '(max-width: 639px)';
const tabs = [
  ['/dashboard', 'Home', Home], ['/my-clubs', 'Clubs', Users],
  ['/host', 'Hosting', CalendarDays], ['/profile', 'Profile', UserRound],
];

// Navigation remains presentation only; routes and server authorization are unchanged.
export function mobileDestination(path, origin = '/dashboard') {
  if (path === '/dashboard') return '/dashboard';
  if (path === '/my-clubs' || path === '/create-club' || path.startsWith('/club/')) return '/my-clubs';
  if (path === '/host' || path === '/host/reports' || path === '/my-trainer-page' || path === '/create-game') return '/host';
  if (/^\/(profile|account-settings|notification-settings|my-history|player-search|admin|rankings)(\/|$)/.test(path)) return '/profile';
  return origin;
}

export function guardedMobileAction(action) {
  const event = new CustomEvent(SAFE_BACK_EVENT, { cancelable: true, detail: { action } });
  if (window.dispatchEvent(event)) action();
}

export function sessionBack(navigate) {
  // Let the existing modal guard consume its sentinel and own the prompt/close.
  // Dispatching SAFE_BACK_EVENT first would prompt twice on dirty session edits.
  if (window.history.state?.__ucGuard) {
    window.history.back();
    return;
  }
  guardedMobileAction(() => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/dashboard', { replace: true });
  });
}

export default function MobileNavigation({ children }) {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  const origin = useRef('/dashboard');
  const entryOrigins = useRef(new Map());
  // Keep native keyboard evidence across a landscape/desktop-width transition.
  const keyboard = useMobileKeyboard(Boolean(user));
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const change = () => setMobile(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  useEffect(() => { origin.current = '/dashboard'; entryOrigins.current.clear(); }, [user?.id]);
  const enabled = mobile && Boolean(user) && !/^\/(reset-password|verify-email-change|confirm|complete-dob)(\/|$)/.test(location.pathname) && location.pathname !== '/';
  const selected = mobileDestination(location.pathname, entryOrigins.current.get(location.key) || origin.current);
  useEffect(() => {
    if (enabled) {
      origin.current = selected;
      entryOrigins.current.set(location.key, selected);
    }
  }, [enabled, selected, location.key]);
  const detail = /^\/games\/[^/]+$/.test(location.pathname);
  const header = enabled && (detail || tabs.some(([path]) => path === location.pathname));
  const go = (path) => { if (path !== location.pathname) guardedMobileAction(() => navigate(path)); };

  return <div className={enabled ? `mobile-navigation-layout${keyboard ? ' mobile-keyboard-open' : ''}` : undefined}>
    {header && <header className="mobile-app-header" aria-label="Open Runs">
      {detail ? <button type="button" onClick={() => sessionBack(navigate)} aria-label="Back to previous screen"><ArrowLeft size={20} /><span>Back</span></button>
        : <img src="/logo-transparent.png" alt="Open Runs USA" />}
      <NotificationBell onNavigate={go} />
    </header>}
    {children}
    {enabled && !keyboard && <nav className="mobile-bottom-navigation" aria-label="Main navigation">
      {tabs.map(([path, label, Icon]) => <button key={path} type="button" aria-current={selected === path ? 'page' : undefined} onClick={() => go(path)}>
        <Icon size={23} aria-hidden="true" /><span>{label}</span>
      </button>)}
    </nav>}
  </div>;
}
